import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { type BusinessKind } from "@/lib/admin-labels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MapPin, Star, SlidersHorizontal, Users, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { LanguageSwitcher, pickDescription, tKind, tKindPlural, useT } from "@/lib/i18n";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import mascotAsset from "@/assets/mascot.png.asset.json";
import dcLogoAsset from "@/assets/dc-logo.png.asset.json";
import {
  listPublicUserLocations,
  listMemberLocationsWithoutBusiness,
  getMyApproxLocationStatus,
  updateMyApproxLocation,
  type PublicUserLocation,
} from "@/lib/user-location.functions";
import {
  listAnonymousVisitorLocations,
  trackAnonymousVisit,
  type AnonVisitorLocation,
} from "@/lib/anon-visitor.functions";
import { listCatalogMapPins, listPublicCatalogMapPins, type CatalogMapPin } from "@/lib/catalog-map.functions";
import { computeDebugAllowed, MAPA_DEBUG_FLAG_KEY, type MapaDebugFlag } from "@/lib/mapa-debug-gate";
import {
  getCatalogMapAudit,
  listCatalogMapAuditRows,
  listCatalogCountryCoverage,
  type CatalogMapAudit,
  type CatalogMapAuditRow,
  type CountryCoverageRow,
} from "@/lib/catalog-map-audit.functions";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { ReferrerMessageDialog } from "@/components/mapa/ReferrerMessageDialog";
import { useAuth } from "@/hooks/useAuth";
import { RequestAccessDialog } from "@/components/RequestAccessDialog";
import { logMapaAccess } from "@/lib/mapa-access-log.functions";
import { currentVisitSource } from "@/lib/visit-source";
import { getActivationStats, type ActivationStat } from "@/lib/activation-stats.functions";
import { listRatingSummaries, type RatingSummary } from "@/lib/reviews.functions";


const KIND_SLUGS = ["restaurace","bar","kavarna","cajovna","stanek","jidelna","mlecny_bar","food_truck","rychle_obcerstveni","bistro"] as const;

import { MAPA_META, pickMeta } from "@/lib/page-meta";

export const Route = createFileRoute("/mapa")({
  ssr: false,
  component: MapaGate,
  validateSearch: (search: Record<string, unknown>): { kind?: string; lang?: string } => {
    const raw = typeof search.kind === "string" ? search.kind : "";
    const kinds = raw.split(",").map((s) => s.trim()).filter((s): s is BusinessKind =>
      (KIND_SLUGS as readonly string[]).includes(s),
    );
    return {
      kind: kinds.length > 0 ? kinds.join(",") : undefined,
      lang: typeof search.lang === "string" ? search.lang : undefined,
    };
  },
  head: ({ match }) => {
    const m = pickMeta(MAPA_META, (match.search as { lang?: string }).lang);
    const url = "https://cerveza.international/mapa";
    return {
      meta: [
        { title: m.title },
        { name: "description", content: m.description },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: m.title },
        { property: "og:description", content: m.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});


const KIND_OPTIONS: BusinessKind[] = [
  "restaurace", "bar", "kavarna", "cajovna", "stanek", "jidelna", "mlecny_bar", "food_truck", "rychle_obcerstveni", "bistro",
];

const KIND_COLORS: Record<BusinessKind, string> = {
  restaurace: "#b54225", bar: "#8a4a8a", kavarna: "#7a4a1f", cajovna: "#3d7a4a",
  stanek: "#c97f1a", jidelna: "#a8911f", mlecny_bar: "#5a82b5", food_truck: "#c94a3a",
  rychle_obcerstveni: "#d97706", bistro: "#6b5b3f",
};

const KIND_EMOJI: Record<BusinessKind, string> = {
  restaurace: "🍽️", bar: "🍸", kavarna: "☕", cajovna: "🍵",
  stanek: "🌮", jidelna: "🥗", mlecny_bar: "🥛", food_truck: "🚚",
  rychle_obcerstveni: "🍔", bistro: "🥪",
};

// Mapping běžných názvů zemí (CZ/EN) na kontinent
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  // Evropa
  "česká republika": "Evropa", "cesko": "Evropa", "česko": "Evropa", "czechia": "Evropa", "czech republic": "Evropa",
  "slovensko": "Evropa", "slovakia": "Evropa", "polsko": "Evropa", "poland": "Evropa",
  "německo": "Evropa", "nemecko": "Evropa", "germany": "Evropa", "rakousko": "Evropa", "austria": "Evropa",
  "maďarsko": "Evropa", "madarsko": "Evropa", "hungary": "Evropa", "francie": "Evropa", "france": "Evropa",
  "španělsko": "Evropa", "spanelsko": "Evropa", "spain": "Evropa", "itálie": "Evropa", "italie": "Evropa", "italy": "Evropa",
  "velká británie": "Evropa", "velka britanie": "Evropa", "united kingdom": "Evropa", "uk": "Evropa",
  "nizozemsko": "Evropa", "netherlands": "Evropa", "belgie": "Evropa", "belgium": "Evropa",
  "portugalsko": "Evropa", "portugal": "Evropa", "švýcarsko": "Evropa", "svycarsko": "Evropa", "switzerland": "Evropa",
  "irsko": "Evropa", "ireland": "Evropa", "řecko": "Evropa", "recko": "Evropa", "greece": "Evropa",
  "chorvatsko": "Evropa", "croatia": "Evropa", "slovinsko": "Evropa", "slovenia": "Evropa",
  "rumunsko": "Evropa", "romania": "Evropa", "bulharsko": "Evropa", "bulgaria": "Evropa",
  "ukrajina": "Evropa", "ukraine": "Evropa", "švédsko": "Evropa", "svedsko": "Evropa", "sweden": "Evropa",
  "norsko": "Evropa", "norway": "Evropa", "dánsko": "Evropa", "dansko": "Evropa", "denmark": "Evropa",
  "finsko": "Evropa", "finland": "Evropa",
  // Severní Amerika
  "usa": "Severní Amerika", "spojené státy": "Severní Amerika", "united states": "Severní Amerika",
  "kanada": "Severní Amerika", "canada": "Severní Amerika", "mexiko": "Severní Amerika", "mexico": "Severní Amerika",
  // Jižní Amerika
  "brazílie": "Jižní Amerika", "brazilie": "Jižní Amerika", "brazil": "Jižní Amerika",
  "argentina": "Jižní Amerika", "chile": "Jižní Amerika", "peru": "Jižní Amerika", "kolumbie": "Jižní Amerika", "colombia": "Jižní Amerika",
  // Asie
  "čína": "Asie", "cina": "Asie", "china": "Asie", "japonsko": "Asie", "japan": "Asie",
  "vietnam": "Asie", "thajsko": "Asie", "thailand": "Asie", "indie": "Asie", "india": "Asie",
  "indonésie": "Asie", "indonesie": "Asie", "indonesia": "Asie", "korea": "Asie", "jižní korea": "Asie",
  // Afrika
  "egypt": "Afrika", "maroko": "Afrika", "morocco": "Afrika", "jihoafrická republika": "Afrika", "south africa": "Afrika",
  // Oceánie
  "austrálie": "Oceánie", "australie": "Oceánie", "australia": "Oceánie", "nový zéland": "Oceánie", "new zealand": "Oceánie",
};

function continentOf(country: string): string {
  return COUNTRY_TO_CONTINENT[country.trim().toLowerCase()] ?? "Ostatní";
}

const CONTINENTS = ["Evropa", "Severní Amerika", "Jižní Amerika", "Asie", "Afrika", "Oceánie", "Ostatní"];
const CONTINENT_CODE_TO_NAME: Record<string, string> = {
  EU: "Evropa",
  NA: "Severní Amerika",
  SA: "Jižní Amerika",
  AS: "Asie",
  AF: "Afrika",
  OC: "Oceánie",
  AN: "Ostatní",
};
const CONTINENT_I18N: Record<string, string> = {
  "Evropa": "mapa.continent.europe",
  "Severní Amerika": "mapa.continent.northAmerica",
  "Jižní Amerika": "mapa.continent.southAmerica",
  "Asie": "mapa.continent.asia",
  "Afrika": "mapa.continent.africa",
  "Oceánie": "mapa.continent.oceania",
  "Ostatní": "mapa.continent.other",
};

type Pin = {
  id: string;
  name: string;
  kind: BusinessKind;
  city: string;
  country: string;
  region: string | null;
  postal_code: string | null;
  latitude: number;
  longitude: number;
  description: string | null;
  descriptions: Record<string, string> | null;
};

const ALL = "__all__";

// Federace: země, kde má smysl vždy nabídnout volbu regionu/státu
const FEDERATION_COUNTRIES = new Set<string>([
  "usa", "spojené státy", "united states", "united states of america",
  "kanada", "canada",
  "austrálie", "australie", "australia",
  "německo", "nemecko", "germany",
  "mexiko", "mexico",
  "brazílie", "brazilie", "brazil",
  "indie", "india",
  "švýcarsko", "svycarsko", "switzerland",
  "španělsko", "spanelsko", "spain",
  "rakousko", "austria",
  "belgie", "belgium",
  "spojené arabské emiráty", "uae", "united arab emirates",
  "česká republika", "cesko", "česko", "czechia", "czech republic", // Kraj
]);
const isFederation = (c: string) => FEDERATION_COUNTRIES.has(c.trim().toLowerCase());
// Federace podle ISO2 (country ve filtrech je nově vždy ISO2 kód).
const FEDERATION_ISO2 = new Set<string>(["US","CA","AU","DE","MX","BR","IN","CH","ES","AT","BE","AE","CZ"]);
const isFederationIso2 = (iso2: string) => FEDERATION_ISO2.has(iso2.toUpperCase());

const MAPA_LANG_STORAGE_KEY = "lc_lang_mapa";
const MAPA_SUPPORTED_LANGS = ["cs","pl","en","es","de","pt","fr","hr","sk","ro","it","hu","bg"] as const;
type MapaLang = typeof MAPA_SUPPORTED_LANGS[number];
const isMapaLang = (v: string | null | undefined): v is MapaLang =>
  !!v && (MAPA_SUPPORTED_LANGS as readonly string[]).includes(v);

function MapaGate() {
  const { user, loading } = useAuth();
  const currentSearch = useRouterState({ select: (s: { location: { searchStr?: string } }) => s.location.searchStr ?? "" });
  const redirectTarget = `/mapa${currentSearch ? (currentSearch.startsWith("?") ? currentSearch : `?${currentSearch}`) : ""}`;

  useEffect(() => {
    if (loading) return;
    const lang = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("lang") : null;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null;
    logMapaAccess({
      data: {
        result: user ? "allowed" : "anonymous",
        path: redirectTarget,
        lang,
        userAgent: ua,
        email: user?.email ?? null,
        userId: user?.id ?? null,
        source: currentVisitSource(),
      },
    }).catch(() => { /* fire-and-forget */ });
  }, [loading, user, redirectTarget]);

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        Načítám…
      </div>
    );
  }
  // Mapa je nyní veřejná: nepřihlášení uživatelé vidí veřejnou variantu
  // (publikované podniky + katalog pinů), ale mohou v ní vyhledávat.
  return <MapaPage publicMode={!user} />;
}

export function MapaPublicPage() {
  return <MapaPage publicMode />;
}


function MapaPage({ publicMode = false }: { publicMode?: boolean } = {}) {
  const { t, lang, setLang } = useT();
  const { kind: kindSearch, lang: langSearch } = Route.useSearch();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  // Feature flag `mapa_debug_feature` in app_settings allows opting selected
  // users (nebo globálně) into the debug panel bez závislosti na DEV režimu.
  // Value shape: { enabled_global?: boolean, user_ids?: string[], tenants?: string[] }
  const [debugFlag, setDebugFlag] = useState<MapaDebugFlag>(null);
  useEffect(() => {
    if (!user) { setDebugFlag(null); return; }
    let cancelled = false;
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", MAPA_DEBUG_FLAG_KEY)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setDebugFlag((data?.value as MapaDebugFlag) ?? null);
      });
    return () => { cancelled = true; };
  }, [user?.id]);
  // Track viewport for responsive debug-panel gating. On mobile the panel is
  // restricted to admins only (catalog_editor / feature-flag users see it on
  // desktop where the extra column space is available).
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  const debugAllowed = computeDebugAllowed({
    isDev: import.meta.env.DEV,
    isMobile,
    hasAdmin: hasRole("admin"),
    hasCatalogEditor: hasRole("catalog_editor"),
    flag: debugFlag,
    userId: user?.id ?? null,
  });

  // Persist language preference specifically for /mapa and restore it without
  // a page reload on subsequent visits. Priority: ?lang= URL param wins (so
  // shared links keep working), else the mapa-scoped stored preference, else
  // whatever the global provider decided. Any manual change on this page is
  // saved back and reflected in the URL via a shallow replace (no reload).
  const didInitLangRef = useRef(false);
  useEffect(() => {
    if (didInitLangRef.current) return;
    didInitLangRef.current = true;
    try {
      if (isMapaLang(langSearch)) {
        if (langSearch !== lang) setLang(langSearch);
        localStorage.setItem(MAPA_LANG_STORAGE_KEY, langSearch);
        return;
      }
      const stored = localStorage.getItem(MAPA_LANG_STORAGE_KEY);
      if (isMapaLang(stored) && stored !== lang) setLang(stored);
    } catch { /* storage unavailable — silently fall back to provider lang */ }
  }, [langSearch, lang, setLang]);

  useEffect(() => {
    if (!didInitLangRef.current) return;
    try { localStorage.setItem(MAPA_LANG_STORAGE_KEY, lang); } catch { /* ignore */ }
    if (langSearch && langSearch !== lang) {
      // Keep URL in sync so copy/paste of the current URL preserves the choice.
      void navigate({
        to: "/mapa",
        search: (prev: Record<string, unknown>) => ({ ...prev, lang }),
        replace: true,
      });
    }
  }, [lang, langSearch, navigate]);


  const [activeKinds, setActiveKinds] = useState<Set<BusinessKind>>(() => {
    if (kindSearch) {
      const arr = kindSearch.split(",").filter((s: string): s is BusinessKind =>
        (KIND_SLUGS as readonly string[]).includes(s),
      );

      if (arr.length > 0) return new Set(arr);
    }
    return new Set(KIND_OPTIONS);
  });

  const [search, setSearch] = useState("");
  const [continent, setContinent] = useState<string>(ALL);
  const [country, setCountry] = useState<string>(ALL);
  const [region, setRegion] = useState<string>(ALL);
  const [city, setCity] = useState<string>(ALL);
  const [postal, setPostal] = useState("");
  const [showPartners, setShowPartners] = useState(true);
  const [showMembers, setShowMembers] = useState(true);
  const [showMembersNoBusiness, setShowMembersNoBusiness] = useState(true);
  const [showAnon, setShowAnon] = useState(true);
  const [showBreweries, setShowBreweries] = useState(true);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const [leaflet, setLeaflet] = useState<any>(null);
  const [msgTarget, setMsgTarget] = useState<string | null>(null);

  // Popupy Leafletu jsou čisté HTML, takže tlačítko „Napsat doporučiteli“
  // odchytáváme delegovaným klikem na kontejneru mapy.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onClick = (ev: Event) => {
      const target = (ev.target as HTMLElement | null)?.closest?.("[data-msg-user]");
      const uid = target?.getAttribute("data-msg-user");
      if (uid) {
        ev.preventDefault();
        setMsgTarget(uid);
      }
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [leaflet]);

  const { data: pins = [], isLoading } = useQuery({
    queryKey: ["approved-businesses-map"],
    queryFn: async () => {
      const cols =
        "id, name, kind, city, country, region, postal_code, latitude, longitude, description, descriptions";
      const { data, error } = await supabase
        .from("businesses")
        .select(cols)
        .eq("status", "approved")
        .not("latitude", "is", null)
        .not("longitude", "is", null);
      if (error) throw error;
      const rows = (data ?? []) as Pin[];

      // Vlastní podniky (i čekající na schválení) vidí jejich majitel hned po
      // přidání — RLS politika "owner read own" to na serveru povoluje.
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (uid) {
        const { data: mine } = await supabase
          .from("businesses")
          .select(cols)
          .eq("owner_id", uid)
          .not("latitude", "is", null)
          .not("longitude", "is", null);
        const seen = new Set(rows.map((r) => r.id));
        for (const m of (mine ?? []) as Pin[]) if (!seen.has(m.id)) rows.push(m);
      }
      return rows;
    },
  });

  const { data: linksByBusiness = {} } = useQuery<Record<string, { url: string; label: string | null }[]>>({
    queryKey: ["map-active-links"],
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("business_links")
        .select("business_id, url, label, expires_at")
        .eq("status", "active")
        .or(`expires_at.is.null,expires_at.gt.${nowIso}`);
      if (error) throw error;
      const map: Record<string, { url: string; label: string | null }[]> = {};
      (data ?? []).forEach((l) => { (map[l.business_id] ||= []).push({ url: l.url, label: l.label }); });
      return map;
    },
  });

  // Ratings (avg + count) for businesses — shown as stars inside popups.
  const { data: ratingRows = [] } = useQuery<RatingSummary[]>({
    queryKey: ["business-rating-summaries"],
    queryFn: () => listRatingSummaries({ data: { targetType: "business" } }),
    staleTime: 60_000,
  });
  const ratingsById = useMemo(() => {
    const m: Record<string, RatingSummary> = {};
    for (const r of ratingRows) m[r.targetId] = r;
    return m;
  }, [ratingRows]);

  // Registered members (anonymized): only user_id + fuzzed lat/lng + country.
  const { data: userPins = [] } = useQuery<PublicUserLocation[]>({
    queryKey: ["public-user-locations"],
    queryFn: () => listPublicUserLocations(),
    staleTime: 60_000,
  });

  // Members who do not own any business — candidates for referrals & ratings.
  const { data: memberNoBusinessPins = [] } = useQuery<PublicUserLocation[]>({
    queryKey: ["member-locations-without-business"],
    queryFn: () => listMemberLocationsWithoutBusiness(),
    staleTime: 60_000,
  });

  // Aktivace (admin-only): návštěvy podle zdroje + příjem, shodná data
  // s kartami v /admin/doporuceni.
  const isAdmin = hasRole("admin");
  const { data: activation } = useQuery<{ stats: ActivationStat[] }>({
    queryKey: ["activation-stats"],
    queryFn: () => getActivationStats(),
    enabled: isAdmin,
    staleTime: 60_000,
  });
  const activationById = useMemo(() => {
    const m: Record<string, ActivationStat> = {};
    for (const s of activation?.stats ?? []) m[s.user_id] = s;
    return m;
  }, [activation]);

  // Anonymous visitors from the last 24h (fuzzed lat/lng + country only).
  const { data: anonPins = [] } = useQuery<AnonVisitorLocation[]>({
    queryKey: ["public-anon-visitor-locations"],
    queryFn: () => listAnonymousVisitorLocations(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Cerveza Data Standard catalog pins (Venues + Brewery Sites).
  // Only admin/catalog_editor can call listCatalogMapPins (returns unpublished
  // sites too). Regular signed-in users use the public feed to avoid a 403
  // that would blank out the map.
  const canSeeAllPins = hasRole("admin") || hasRole("catalog_editor");
  const useAllPins = !publicMode && canSeeAllPins;
  const { data: catalogPins = [] } = useQuery<CatalogMapPin[]>({
    queryKey: ["catalog-map-pins", useAllPins ? "all" : "public"],
    queryFn: () => (useAllPins ? listCatalogMapPins() : listPublicCatalogMapPins()),
    staleTime: 5 * 60_000,
  });

  // Audit: DB-side count of what SHOULD appear on the map (published + GPS).
  const { data: mapAudit } = useQuery<CatalogMapAudit>({
    queryKey: ["catalog-map-audit"],
    queryFn: () => getCatalogMapAudit(),
    staleTime: 5 * 60_000,
  });

  // Full DB list of published+GPS entries — used to compute the "missing on map" diff.
  const { data: auditRows = [] } = useQuery<CatalogMapAuditRow[]>({
    queryKey: ["catalog-map-audit-rows"],
    queryFn: () => listCatalogMapAuditRows(),
    staleTime: 5 * 60_000,
  });

  // Per-country coverage overview (published/imported/not imported).
  const { data: countryCoverage = [] } = useQuery<CountryCoverageRow[]>({
    queryKey: ["catalog-country-coverage"],
    queryFn: () => listCatalogCountryCoverage(),
    staleTime: 5 * 60_000,
  });

  // Fire-and-forget: ping anonymous-visit tracking on mount, once per session.
  // Signed-in users are handled by updateMyApproxLocation from __root.tsx —
  // no need to run both, so guard on the current session.
  const qc = useQueryClient();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (cancelled || sess.session) return;
      try {
        const res = await trackAnonymousVisit();
        if (!cancelled && res?.ok) {
          await qc.invalidateQueries({ queryKey: ["public-anon-visitor-locations"] });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [qc]);



  // Businesses' owners are already represented by their business pin — skip those users
  // to avoid stacking two pins on the same person.
  const ownerIds = useMemo(() => {
    // owner_id is not selected on `pins`; approximation: hide all user pins whose id
    // matches any approved business owner would require another query. Cheap enough: leave all
    // users visible — the fuzz already separates them from their exact business pin.
    return new Set<string>();
  }, []);

  const partnerSet = useMemo(() => {
    const s = new Set<string>();
    Object.entries(linksByBusiness).forEach(([id, links]) => {
      if (links.some((l) => /^https?:\/\/([a-z0-9-]+\.)*(directclub\.digital|direct\.club)(\/|$|\?|#)/i.test(l.url))) {
        s.add(id);
      }
    });
    return s;
  }, [linksByBusiness]);

  // Reset navazujících filtrů, když změním nadřazený
  useEffect(() => { setCountry(ALL); setRegion(ALL); setCity(ALL); }, [continent]);
  useEffect(() => { setRegion(ALL); setCity(ALL); }, [country]);
  useEffect(() => { setCity(ALL); }, [region]);

  // Načti Leaflet jen v prohlížeči
  useEffect(() => {
    let mounted = true;
    (async () => {
      const Lmod = await import("leaflet");
      const L = (Lmod as unknown as { default?: unknown }).default ?? Lmod;
      // leaflet.markercluster is a UMD plugin that augments the global L —
      // expose it on window BEFORE importing so `L.markerClusterGroup` exists.
      (window as unknown as { L: unknown }).L = L;
      await import("leaflet/dist/leaflet.css");
      await import("leaflet.markercluster");
      await import("leaflet.markercluster/dist/MarkerCluster.css");
      await import("leaflet.markercluster/dist/MarkerCluster.Default.css");
      if (mounted) setLeaflet(L);
    })();
    return () => { mounted = false; };
  }, []);


  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = leaflet;
    const map = L.map(containerRef.current, { zoomControl: true }).setView([30, 10], 2);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: t("mapa.attribution"),
    }).addTo(map);
    mapRef.current = map;
    // Cluster group: piny se slučují při oddálení, oddělují se při přiblížení.
    layerRef.current = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 16,
      maxClusterRadius: 55,
      chunkedLoading: true,
    }).addTo(map);
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, [leaflet]);


  // Metadata států z katalogu (Cerveza Data Standard). Businesses tabulka
  // ukládá stát jako ISO2 kód, katalog také — sjednotíme obě datové sady
  // podle ISO2 a v UI zobrazíme čitelný název státu (short_name / local_name).
  const catalogCountryMeta = useMemo(() => {
    const m = new Map<string, { label: string; continent: string }>();
    for (const c of catalogPins) {
      const iso2 = (c.country_iso2 ?? "").toUpperCase();
      if (!iso2) continue;
      if (m.has(iso2)) continue;
      m.set(iso2, {
        label: c.country_name ?? iso2,
        continent: CONTINENT_CODE_TO_NAME[c.continent_code ?? ""] ?? "Ostatní",
      });
    }
    return m;
  }, [catalogPins]);

  const continentOfIso2 = (iso2: string): string => {
    const meta = catalogCountryMeta.get(iso2.toUpperCase());
    if (meta) return meta.continent;
    return continentOf(iso2);
  };

  // Dostupné hodnoty pro kaskádové filtry. Sjednoť ISO2 kódy z podniků
  // (businesses.country) i z katalogu (cid_countries.iso2), ať se ve výběru
  // objeví každý stát, pro který v DB existuje aspoň jeden záznam.
  const countriesForContinent = useMemo(() => {
    const set = new Set<string>();
    pins.forEach((p) => {
      const iso = (p.country ?? "").toUpperCase();
      if (!iso) return;
      if (continent === ALL || continentOfIso2(iso) === continent) set.add(iso);
    });
    catalogPins.forEach((c) => {
      const iso = (c.country_iso2 ?? "").toUpperCase();
      if (!iso) return;
      if (continent === ALL || continentOfIso2(iso) === continent) set.add(iso);
    });
    return Array.from(set)
      .map((code) => ({ code, label: catalogCountryMeta.get(code)?.label ?? code }))
      .sort((a, b) => a.label.localeCompare(b.label, "cs"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, catalogPins, catalogCountryMeta, continent]);

  const regionsForCountry = useMemo(() => {
    if (country === ALL) return [] as string[];
    const set = new Set<string>();
    pins.forEach((p) => {
      if ((p.country ?? "").toUpperCase() !== country) return;
      if (p.region) set.add(p.region);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "cs"));
  }, [pins, country]);

  // Region select zobraz jen pro federace nebo když existuje aspoň jeden region
  const showRegion = country !== ALL && (isFederationIso2(country) || regionsForCountry.length > 0);

  const citiesForCountry = useMemo(() => {
    const set = new Set<string>();
    pins.forEach((p) => {
      const iso = (p.country ?? "").toUpperCase();
      if (continent !== ALL && continentOfIso2(iso) !== continent) return;
      if (country !== ALL && iso !== country) return;
      if (region !== ALL && (p.region ?? "") !== region) return;
      if (p.city) set.add(p.city);
    });
    catalogPins.forEach((c) => {
      const iso = (c.country_iso2 ?? "").toUpperCase();
      if (continent !== ALL && continentOfIso2(iso) !== continent) return;
      if (country !== ALL && iso !== country) return;
      if (c.city) set.add(c.city);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "cs"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, catalogPins, continent, country, region]);

  // Precompute a language-independent, lowercased search haystack per pin.
  // Concatenating ALL localized descriptions (any language) lets users search
  // in whatever language they type without making `filtered` depend on `lang`
  // — swapping the UI language must not invalidate the filter memo, tear
  // down markers or trigger a fitBounds recompute.
  const searchHay = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of pins) {
      const allDescriptions = p.descriptions
        ? Object.values(p.descriptions).filter((v): v is string => typeof v === "string").join(" ")
        : "";
      map.set(
        p.id,
        `${p.name} ${p.city} ${p.country} ${p.region ?? ""} ${p.postal_code ?? ""} ${p.description ?? ""} ${allDescriptions}`.toLowerCase(),
      );
    }
    return map;
  }, [pins]);

  // Filtrované piny
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pq = postal.trim().toLowerCase();
    return pins.filter((p) => {
      const iso = (p.country ?? "").toUpperCase();
      if (!activeKinds.has(p.kind)) return false;
      if (continent !== ALL && continentOfIso2(iso) !== continent) return false;
      if (country !== ALL && iso !== country) return false;
      if (region !== ALL && (p.region ?? "") !== region) return false;
      if (city !== ALL && p.city !== city) return false;
      if (pq && !(p.postal_code ?? "").toLowerCase().includes(pq)) return false;
      if (!showPartners && partnerSet.has(p.id)) return false;
      if (q && !(searchHay.get(p.id) ?? "").includes(q)) return false;
      return true;
    });
  }, [pins, activeKinds, search, continent, country, region, city, postal, showPartners, partnerSet, searchHay]);

  // Katalog (Cerveza Data Standard) — sdílí ISO2 kód státu s businesses,
  // takže filtry kontinent / země / město ho zužují stejně jako klasické
  // podniky. Region a PSČ katalog nezná (jiný datový model).
  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cityFilter = city !== ALL ? city.toLowerCase() : "";
    return catalogPins.filter((c) => {
      if (!showBreweries && c.layer === "brewery_site") return false;
      const iso = (c.country_iso2 ?? "").toUpperCase();
      if (continent !== ALL && continentOfIso2(iso) !== continent) return false;
      if (country !== ALL && iso !== country) return false;
      if (cityFilter && (c.city ?? "").toLowerCase() !== cityFilter) return false;
      if (!q) return true;
      const hay = `${c.name} ${c.city ?? ""} ${iso} ${c.country_name ?? ""} ${c.kind ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogPins, search, city, country, continent, showBreweries]);

  const breweriesCount = useMemo(
    () => catalogPins.reduce((n, c) => n + (c.layer === "brewery_site" ? 1 : 0), 0),
    [catalogPins],
  );

  // Track markers by a stable key so we can update popup content in-place on
  // language change (preserves the currently open popup and current viewport).
  const markersRef = useRef<Map<string, any>>(new Map());

  // Build popup HTML — pure function of lang / t / data. Extracted so both the
  // initial draw and the language-change effect can call it with identical output.
  const buildBusinessPopup = (p: Pin, isPartner: boolean) => {
    const typeBadge = isPartner
      ? `<span style="background:#fbbf24;color:#78350f;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px">★ ${escapeHtml(t("mapa.partnerBadge"))}</span>`
      : `<span style="background:#e5e7eb;color:#374151;font-size:10px;font-weight:600;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px">${escapeHtml(tKind(t, p.kind))}</span>`;
    const desc = pickDescription(lang, p.descriptions, p.description);
    const rating = ratingsById[p.id];
    const ratingHtml = rating
      ? `<div style="font-size:12px;margin-top:4px;color:#78350f"><span style="color:#f59e0b">★</span> <strong>${rating.avg.toFixed(1)}</strong>/10 <span style="color:#666">(${rating.count})</span></div>`
      : `<div style="font-size:11px;margin-top:4px;color:#999">${escapeHtml(t("mapa.noRatings") || "Zatím bez hodnocení")}</div>`;
    const addr = `${escapeHtml(p.city)}${p.postal_code ? " " + escapeHtml(p.postal_code) : ""}, ${escapeHtml(p.country)}`;
    return `<div style="min-width:240px;max-width:280px">
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:4px">${typeBadge}${isPartner ? `<span style="background:#e5e7eb;color:#374151;font-size:10px;font-weight:600;padding:2px 8px;border-radius:999px">${escapeHtml(tKind(t, p.kind))}</span>` : ""}</div>
      <div style="font-weight:700;font-size:14px;line-height:1.3">${escapeHtml(p.name)}</div>
      <div style="font-size:12px;color:#666;margin-top:4px">📍 ${addr}</div>
      ${ratingHtml}
      ${isPartner ? `<div style="font-size:11px;color:#78350f;background:#fef3c7;padding:4px 6px;border-radius:4px;margin-top:6px">${escapeHtml(t("mapa.partnerHint") || "Ověřená partnerská firma registrovaná v Cerveza.International")}</div>` : ""}
      ${desc ? `<div style="font-size:12px;margin-top:6px;color:#333">${escapeHtml(desc).slice(0, 200)}${desc.length > 200 ? "…" : ""}</div>` : ""}
      <div style="margin-top:8px"><a href="/podnik/${encodeURIComponent(p.id)}" style="display:inline-block;background:#b54225;color:#fff;font-size:12px;font-weight:600;padding:5px 12px;border-radius:6px;text-decoration:none">${escapeHtml(t("mapa.viewProfile"))} →</a></div>
      ${(linksByBusiness[p.id] ?? []).length > 0 ? `<div style="font-size:12px;margin-top:8px;padding-top:6px;border-top:1px solid #eee">${
        linksByBusiness[p.id].map((l) => `<a href="${escapeHtml(l.url)}" target="_blank" rel="noopener noreferrer" style="display:block;color:#2563eb;text-decoration:none;margin-top:2px">↗ ${escapeHtml(l.label || l.url)}</a>`).join("")
      }</div>` : ""}
    </div>`;
  };

  /** Admin blok v popupu: návštěvy (vč. zdroje) a příjem daného uživatele. */
  const buildActivationBlock = (userId: string) => {
    if (!isAdmin) return "";
    const s = activationById[userId];
    const visits = s?.visits ?? 0;
    const revenue = ((s?.revenue_cents ?? 0) / 100).toFixed(0);
    const src = s?.bySource;
    const srcLine = src
      ? `app ${src.app} · chat ${src.chat} · e-mail ${src.email} · ref ${src.referral}`
      : "";
    return `<div style="margin-top:8px;padding-top:8px;border-top:1px dashed #d1d5db;text-align:left;font-size:11px;color:#374151">
      <div style="font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.4px;font-size:9px">${escapeHtml(t("mapa.activation.title") || "Aktivace (admin)")}</div>
      <div style="margin-top:3px">👣 <strong>${visits}</strong> ${escapeHtml(t("mapa.activation.visits") || "návštěv")} · 💶 <strong>${escapeHtml(revenue)} €</strong> ${escapeHtml(t("mapa.activation.revenue") || "příjem")}</div>
      ${srcLine ? `<div style="color:#6b7280;margin-top:2px">${escapeHtml(srcLine)}</div>` : ""}
      <div style="margin-top:4px"><a href="/admin/doporuceni?user=${encodeURIComponent(userId)}" style="color:#2563eb;text-decoration:none">${escapeHtml(t("mapa.activation.openAdmin") || "Otevřít v Doporučení")} →</a></div>
    </div>`;
  };

  const buildUserPopup = (u: PublicUserLocation) => (
    `<div style="min-width:200px;text-align:center">
      <span style="display:inline-block;background:#b54225;color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px">👤 ${escapeHtml(t("mapa.memberBadge") || "Člen Direct Clubu")}</span>
      <img src="${mascotAsset.url}" alt="" style="width:56px;height:56px;margin:6px auto 0;display:block" />
      <div style="font-weight:700;margin-top:4px;font-size:13px">${escapeHtml(t("mapa.userPin.title"))}</div>
      ${u.country ? `<div style="font-size:12px;color:#666;margin-top:2px">📍 ${escapeHtml(u.country)}</div>` : ""}
      <div style="font-size:11px;color:#999;margin-top:6px;line-height:1.4">${escapeHtml(t("mapa.userPin.subtitle"))}</div>
      <div style="font-size:10px;color:#b54225;margin-top:6px;padding-top:6px;border-top:1px solid #eee">${escapeHtml(t("mapa.memberPrivacyNote") || "Registrovaný uživatel · přibližná pozice pro ochranu soukromí")}</div>
      <button type="button" data-msg-user="${escapeHtml(u.user_id)}" style="margin-top:8px;width:100%;background:#111827;color:#fff;font-size:12px;font-weight:600;padding:6px 10px;border:0;border-radius:6px;cursor:pointer">✉ ${escapeHtml(t("mapa.message.button") || "Napsat doporučiteli")}</button>
      ${buildActivationBlock(u.user_id)}
    </div>`
  );

  const buildAnonPopup = (color: string, label: string, v: AnonVisitorLocation) => (
    `<div style="min-width:200px;text-align:center">
      <span style="display:inline-block;background:${color};color:#fff;font-weight:700;font-size:10px;padding:2px 10px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px">🌐 ${escapeHtml(t("mapa.showAnon"))}</span>
      <div style="font-weight:700;margin-top:8px;font-size:13px">${escapeHtml(t("mapa.anonPin.title"))}</div>
      <div style="font-size:12px;color:#666;margin-top:2px">📍 ${escapeHtml(v.country || label)}</div>
      <div style="font-size:11px;color:#999;margin-top:6px;line-height:1.4">${escapeHtml(t("mapa.anonPin.subtitle"))}</div>
      <div style="font-size:10px;color:#666;margin-top:6px;padding-top:6px;border-top:1px solid #eee">${escapeHtml(t("mapa.anonPrivacyNote") || "Nepřihlášený návštěvník · pozice se zobrazuje 24 h, poté mizí")}</div>
    </div>`
  );

  const buildMemberNoBusinessPopup = (u: PublicUserLocation) => (
    `<div style="min-width:220px;text-align:center">
      <span style="display:inline-block;background:#2563eb;color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px">👤 ${escapeHtml(t("mapa.memberNoBusiness.badge"))}</span>
      <img src="${mascotAsset.url}" alt="" style="width:56px;height:56px;margin:6px auto 0;display:block" />
      <div style="font-weight:700;margin-top:4px;font-size:13px">${escapeHtml(t("mapa.memberNoBusiness.title"))}</div>
      ${u.country ? `<div style="font-size:12px;color:#666;margin-top:2px">📍 ${escapeHtml(u.country)}</div>` : ""}
      <div style="font-size:11px;color:#999;margin-top:6px;line-height:1.4">${escapeHtml(t("mapa.memberNoBusiness.subtitle"))}</div>
      <div style="display:flex;flex-direction:column;gap:6px;margin-top:10px;padding-top:8px;border-top:1px solid #eee">
        <a href="/auth?next=/registrace-podniku" style="display:inline-block;background:#b54225;color:#fff;font-size:12px;font-weight:600;padding:5px 10px;border-radius:6px;text-decoration:none">${escapeHtml(t("mapa.memberNoBusiness.recommendBusiness"))}</a>
        <a href="/moje-podniky" style="display:inline-block;background:#2563eb;color:#fff;font-size:12px;font-weight:600;padding:5px 10px;border-radius:6px;text-decoration:none">${escapeHtml(t("mapa.memberNoBusiness.inviteMember"))}</a>
        <a href="/mapa" style="display:inline-block;background:#f3f4f6;color:#374151;font-size:12px;font-weight:600;padding:5px 10px;border-radius:6px;text-decoration:none">${escapeHtml(t("mapa.memberNoBusiness.rateVisited"))}</a>
      </div>
      <div style="font-size:10px;color:#2563eb;margin-top:6px">${escapeHtml(t("mapa.memberNoBusiness.privacy"))}</div>
      <button type="button" data-msg-user="${escapeHtml(u.user_id)}" style="margin-top:8px;width:100%;background:#111827;color:#fff;font-size:12px;font-weight:600;padding:6px 10px;border:0;border-radius:6px;cursor:pointer">✉ ${escapeHtml(t("mapa.message.button") || "Napsat doporučiteli")}</button>
      ${buildActivationBlock(u.user_id)}
    </div>`
  );

  const REVIEW_COLORS: Record<string, string> = {
    DISCOVERY_ONLY: "#9ca3af",
    PENDING_REVIEW: "#eab308",
    VERIFIED: "#16a34a",
    REJECTED: "#ef4444",
  };
  const REVIEW_I18N: Record<string, string> = {
    DISCOVERY_ONLY: "mapa.review.discovery",
    PENDING_REVIEW: "mapa.review.pending",
    VERIFIED: "mapa.review.verified",
    REJECTED: "mapa.review.rejected",
  };
  const buildCatalogPopup = (c: CatalogMapPin) => {
    const isVenue = c.layer === "venue";
    const emojiBadge = isVenue ? "🍺" : "🏭";
    const badgeBg = isVenue ? "#c2410c" : "#8b4513";
    const label = t(isVenue ? "mapa.catalog.venue" : "mapa.catalog.brewery");
    const review = c.review_status ?? "DISCOVERY_ONLY";
    const detailHref = isVenue ? null : `/provozovna/${c.id}`;
    const reviewBadgeHtml = !isVenue
      ? `<span style="display:inline-block;background:${REVIEW_COLORS[review]};color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px;margin-left:4px">${escapeHtml(t(REVIEW_I18N[review]))}</span>`
      : "";
    return `<div style="min-width:200px;max-width:260px">
      <span style="display:inline-block;background:${badgeBg};color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:0.4px">${emojiBadge} ${escapeHtml(label)}</span>${reviewBadgeHtml}
      <div style="font-weight:700;margin-top:6px;font-size:14px;line-height:1.3">${escapeHtml(c.name)}</div>
      ${c.city ? `<div style="font-size:12px;color:#666;margin-top:4px">📍 ${escapeHtml(c.city)}${c.country_iso2 ? ", " + c.country_iso2 : ""}</div>` : ""}
      <div style="font-size:10px;color:#999;margin-top:6px;padding-top:6px;border-top:1px solid #eee">${escapeHtml(t("mapa.catalog.imported"))}</div>
      ${detailHref ? `<div style="margin-top:8px"><a href="${detailHref}" style="display:inline-block;background:${badgeBg};color:#fff;font-size:12px;font-weight:600;padding:5px 12px;border-radius:6px;text-decoration:none">${escapeHtml(t("mapa.catalog.detail"))} →</a></div>` : ""}
    </div>`;
  };


  // Vykreslení markerů (rebuilt when filter set / data changes)
  // Single source of truth for which members / anonymous visitors are on the
  // map right now. Every effect below reads these instead of recomputing.
  const visibleMembers = useMemo(
    () => (showMembers ? userPins.filter((u) => !ownerIds.has(u.user_id)) : []),
    [showMembers, userPins, ownerIds],
  );
  const visibleMembersNoBusiness = useMemo(
    () => (showMembersNoBusiness ? memberNoBusinessPins : []),
    [showMembersNoBusiness, memberNoBusinessPins],
  );
  const visibleAnon = useMemo(
    () => (showAnon ? anonPins : []),
    [showAnon, anonPins],
  );

  useEffect(() => {
    if (!leaflet || !mapRef.current || !layerRef.current) return;
    const L = leaflet;
    layerRef.current.clearLayers();
    markersRef.current.clear();

    filtered.forEach((p) => {
      const color = KIND_COLORS[p.kind];
      const isPartner = partnerSet.has(p.id);
      const icon = L.divIcon({
        className: "lc-pin",
        html: `<div style="background:${color}" class="lc-pin-dot">${KIND_EMOJI[p.kind]}</div>${isPartner ? '<div class="lc-pin-star">★</div>' : ""}`,
        iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -28],
      });
      const m = L.marker([p.latitude, p.longitude], { icon, zIndexOffset: 1000 });
      m.bindPopup(buildBusinessPopup(p, isPartner));
      m.addTo(layerRef.current);
      markersRef.current.set(`b:${p.id}`, m);
    });

    visibleMembers.forEach((u) => {
      const icon = L.divIcon({
        className: "lc-user-pin",
        html: `<div class="lc-user-pin-wrap">
          <img src="${dcLogoAsset.url}" alt="" class="lc-user-pin-bg" />
          <img src="${mascotAsset.url}" alt="" class="lc-user-pin-mascot" />
        </div>`,
        iconSize: [44, 44], iconAnchor: [22, 40], popupAnchor: [0, -36],
      });
      const m = L.marker([u.latitude, u.longitude], { icon, zIndexOffset: 2000 });
      m.bindPopup(buildUserPopup(u));
      m.addTo(layerRef.current);
      markersRef.current.set(`u:${u.user_id}`, m);
    });

    visibleMembersNoBusiness.forEach((u) => {
      const icon = L.divIcon({
        className: "lc-user-pin lc-member-no-business-pin",
        html: `<div class="lc-user-pin-wrap" style="box-shadow:0 0 0 3px #2563eb,0 2px 6px rgba(0,0,0,0.3);border-radius:50%">
          <img src="${dcLogoAsset.url}" alt="" class="lc-user-pin-bg" />
          <img src="${mascotAsset.url}" alt="" class="lc-user-pin-mascot" />
          <span style="position:absolute;bottom:-2px;right:-2px;background:#2563eb;color:#fff;font-size:9px;font-weight:700;width:16px;height:16px;border-radius:50%;display:grid;place-items:center;border:2px solid #fff">+</span>
        </div>`,
        iconSize: [44, 44], iconAnchor: [22, 40], popupAnchor: [0, -36],
      });
      const m = L.marker([u.latitude, u.longitude], { icon, zIndexOffset: 2100 });
      m.bindPopup(buildMemberNoBusinessPopup(u));
      m.addTo(layerRef.current);
      markersRef.current.set(`n:${u.user_id}`, m);
    });

    visibleAnon.forEach((v, idx) => {
      const cc = (v.country ?? "").toUpperCase();
      const color = flagColorFor(cc);
      const label = cc || "•";
      const icon = L.divIcon({
        className: "lc-anon-pin",
        html: `<div class="lc-user-flag-pin">
          <svg viewBox="0 0 32 44" width="28" height="38" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 43 C6 30 2 22 2 14 A14 14 0 1 1 30 14 C30 22 26 30 16 43 Z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
            <circle cx="16" cy="14" r="8" fill="#ffffff" fill-opacity="0.92"/>
            <text x="16" y="17" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="9" font-weight="700" fill="#111827">${escapeHtml(label)}</text>
          </svg>
        </div>`,
        iconSize: [28, 38], iconAnchor: [14, 36], popupAnchor: [0, -32],
      });
      const m = L.marker([v.latitude, v.longitude], { icon, zIndexOffset: 1500 });
      m.bindPopup(buildAnonPopup(color, label, v));
      m.addTo(layerRef.current);
      markersRef.current.set(`a:${idx}:${v.latitude},${v.longitude}`, m);
    });

    // Cerveza Data Standard catalog: Venues (glass) + Brewery Sites (barrel)
    filteredCatalog.forEach((c) => {
      const isVenue = c.layer === "venue";
      const bg = isVenue ? "#c2410c" : "#8b4513";
      const emoji = isVenue ? "🍺" : "🏭";
      const review = c.review_status ?? "DISCOVERY_ONLY";
      const ringColor = !isVenue ? REVIEW_COLORS[review] ?? "#fdfaf3" : "#fdfaf3";
      const ringWidth = !isVenue && review !== "DISCOVERY_ONLY" ? 3 : 2;
      const icon = L.divIcon({
        className: "lc-catalog-pin",
        html: `<div style="background:${bg};width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;border:${ringWidth}px solid ${ringColor};box-shadow:0 2px 6px rgba(0,0,0,0.3)">${emoji}</div>`,
        iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28],
      });
      const m = L.marker([c.latitude, c.longitude], { icon, zIndexOffset: 0 });
      m.bindPopup(buildCatalogPopup(c));
      m.addTo(layerRef.current);
      markersRef.current.set(`c:${c.layer}:${c.id}`, m);
    });
    // NOTE: intentionally NOT depending on `t` / `lang` here — popup translations
    // are updated in place by a separate effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, leaflet, linksByBusiness, partnerSet, visibleMembers, visibleMembersNoBusiness, visibleAnon, filteredCatalog, ratingsById]);

  // Fit bounds only when the visible dataset changes (not on language switch).
  useEffect(() => {
    if (!leaflet || !mapRef.current) return;
    const allCoords: [number, number][] = [
      ...filtered.map((p) => [p.latitude, p.longitude] as [number, number]),
      ...visibleMembers.map((u) => [u.latitude, u.longitude] as [number, number]),
      ...visibleMembersNoBusiness.map((u) => [u.latitude, u.longitude] as [number, number]),
      ...visibleAnon.map((v) => [v.latitude, v.longitude] as [number, number]),
      ...filteredCatalog.map((c) => [c.latitude, c.longitude] as [number, number]),
    ];
    if (allCoords.length > 0) {
      const bounds = leaflet.latLngBounds(allCoords);
      mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [filtered, leaflet, visibleMembers, visibleMembersNoBusiness, visibleAnon, filteredCatalog]);

  // Re-translate popup content on language change without tearing down markers.
  // Preserves current viewport and the currently open popup.
  useEffect(() => {
    if (!leaflet || markersRef.current.size === 0) return;
    filtered.forEach((p) => {
      const m = markersRef.current.get(`b:${p.id}`);
      if (m) m.setPopupContent(buildBusinessPopup(p, partnerSet.has(p.id)));
    });
    visibleMembers.forEach((u) => {
      const m = markersRef.current.get(`u:${u.user_id}`);
      if (m) m.setPopupContent(buildUserPopup(u));
    });
    visibleMembersNoBusiness.forEach((u) => {
      const m = markersRef.current.get(`n:${u.user_id}`);
      if (m) m.setPopupContent(buildMemberNoBusinessPopup(u));
    });
    visibleAnon.forEach((v, idx) => {
      const m = markersRef.current.get(`a:${idx}:${v.latitude},${v.longitude}`);
      if (!m) return;
      const cc = (v.country ?? "").toUpperCase();
      m.setPopupContent(buildAnonPopup(flagColorFor(cc), cc || "•", v));
    });
    filteredCatalog.forEach((c) => {
      const m = markersRef.current.get(`c:${c.layer}:${c.id}`);
      if (m) m.setPopupContent(buildCatalogPopup(c));
    });
    // `t` follows `lang` from the same context — depending on `lang` alone
    // avoids re-running on every parent render (t is a fresh ref each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, linksByBusiness, activationById]);




  const counts = useMemo(() => {
    const c: Partial<Record<BusinessKind, number>> = {};
    pins.forEach((p) => { c[p.kind] = (c[p.kind] ?? 0) + 1; });
    return c;
  }, [pins]);

  const toggleKind = (k: BusinessKind) => {
    setActiveKinds((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };

  const resetFilters = () => {
    setSearch(""); setContinent(ALL); setCountry(ALL); setRegion(ALL); setCity(ALL); setPostal("");
    setShowPartners(true); setActiveKinds(new Set(KIND_OPTIONS)); setShowMembers(true); setShowMembersNoBusiness(true); setShowAnon(true); setShowBreweries(true);
  };

  type LegendKey = "breweries" | "partners" | "anon" | "members" | "membersNoBusiness";
  const highlightLegend = (kind: LegendKey | null) => {
    if (!mapRef.current) return;
    const container = mapRef.current.getContainer?.() as HTMLElement | undefined;
    if (!container) return;
    if (!kind) {
      container.classList.remove("lc-legend-dim");
      markersRef.current.forEach((m) => {
        const el = m?._icon as HTMLElement | undefined;
        el?.classList.remove("lc-legend-hi");
      });
      return;
    }
    container.classList.add("lc-legend-dim");
    markersRef.current.forEach((m, key) => {
      const el = m?._icon as HTMLElement | undefined;
      if (!el) return;
      let match = false;
      if (kind === "breweries") match = key.startsWith("c:brewery_site:");
      else if (kind === "partners") match = key.startsWith("b:") && partnerSet.has(key.slice(2));
      else if (kind === "anon") match = key.startsWith("a:");
      else if (kind === "members") match = key.startsWith("u:");
      else if (kind === "membersNoBusiness") match = key.startsWith("n:");
      el.classList.toggle("lc-legend-hi", match);
    });
  };


  const filtersPanel = (
    <>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("mapa.filters.search")}</h2>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("mapa.filters.searchPlaceholder")}
        className="mb-4"
      />

      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("mapa.filters.location")}</h2>
      <div className="mb-4 space-y-2">
        <Select value={continent} onValueChange={setContinent}>
          <SelectTrigger><SelectValue placeholder={t("mapa.filters.continent")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("mapa.filters.allContinents")}</SelectItem>
            {CONTINENTS.map((c) => <SelectItem key={c} value={c}>{t(CONTINENT_I18N[c])}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={country} onValueChange={setCountry} disabled={countriesForContinent.length === 0}>
          <SelectTrigger><SelectValue placeholder={t("mapa.filters.country")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("mapa.filters.allCountries")}</SelectItem>
            {countriesForContinent.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>

        {showRegion && (
          <Select value={region} onValueChange={setRegion} disabled={regionsForCountry.length === 0}>
            <SelectTrigger>
              <SelectValue placeholder={regionsForCountry.length === 0 ? t("mapa.filters.regionEmpty") : t("mapa.filters.region")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("mapa.filters.allRegions")}</SelectItem>
              {regionsForCountry.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={city} onValueChange={setCity} disabled={citiesForCountry.length === 0}>
          <SelectTrigger><SelectValue placeholder={t("mapa.filters.city")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("mapa.filters.allCities")}</SelectItem>
            {citiesForCountry.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Input
          value={postal}
          onChange={(e) => setPostal(e.target.value)}
          placeholder={t("mapa.filters.postal")}
        />
      </div>

      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("mapa.filters.partnership")}</h2>
      <button
        onClick={() => setShowPartners((v) => !v)}
        className={`mb-2 flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition ${
          showPartners ? "border-amber-400 bg-amber-50 text-amber-900" : "border-transparent bg-transparent opacity-50"
        }`}
      >
        <span className="flex items-center gap-2"><Star className="h-4 w-4" /> {t("mapa.partnerFilter")}</span>
        <span className="text-xs text-muted-foreground">{partnerSet.size}</span>
      </button>

      <button
        onClick={() => setShowMembers((v) => !v)}
        className={`mb-2 flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition ${
          showMembers ? "border-foreground/20 bg-background" : "border-transparent bg-transparent opacity-50"
        }`}
      >
        <span className="flex items-center gap-2"><Users className="h-4 w-4" /> {t("mapa.showUsers")}</span>
        <span className="text-xs text-muted-foreground">{userPins.length}</span>
      </button>

      <button
        onClick={() => setShowMembersNoBusiness((v) => !v)}
        className={`mb-2 flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition ${
          showMembersNoBusiness ? "border-foreground/20 bg-background" : "border-transparent bg-transparent opacity-50"
        }`}
      >
        <span className="flex items-center gap-2"><Users className="h-4 w-4" /> {t("mapa.showMembersNoBusiness")}</span>
        <span className="text-xs text-muted-foreground">{memberNoBusinessPins.length}</span>
      </button>

      <button
        onClick={() => setShowAnon((v) => !v)}
        className={`mb-4 flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition ${
          showAnon ? "border-foreground/20 bg-background" : "border-transparent bg-transparent opacity-50"
        }`}
      >
        <span className="flex items-center gap-2"><Users className="h-4 w-4" /> {t("mapa.showAnon")}</span>
        <span className="text-xs text-muted-foreground">{anonPins.length}</span>
      </button>


      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("mapa.kindHeader")}
      </h2>
      <div className="space-y-1.5">
        {KIND_OPTIONS.map((k) => {
          const isActive = activeKinds.has(k);
          const count = counts[k] ?? 0;
          return (
            <button
              key={k}
              onClick={() => toggleKind(k)}
              title={`${tKindPlural(t, k)} — ${count}`}
              className={`flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition ${
                isActive ? "border-foreground/20 bg-background" : "border-transparent bg-transparent opacity-50"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="grid h-5 w-5 place-items-center rounded-full text-xs"
                  style={{ background: KIND_COLORS[k], color: "white" }}>{KIND_EMOJI[k]}</span>
                {tKindPlural(t, k)}
              </span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </button>

          );
        })}
        <button
          onClick={() => setShowBreweries((v) => !v)}
          className={`flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition ${
            showBreweries ? "border-foreground/20 bg-background" : "border-transparent bg-transparent opacity-50"
          }`}
        >
          <span className="flex items-center gap-2">
            <span
              className="grid h-5 w-5 place-items-center rounded-full text-xs"
              style={{ background: "#8b4513", color: "white" }}
            >
              🏭
            </span>
            {t("mapa.breweries")}
          </span>
          <span className="text-xs text-muted-foreground">{breweriesCount}</span>
        </button>
      </div>

      <div className="mt-4 space-y-2">
        <Button size="sm" variant="ghost" className="w-full" onClick={resetFilters}>{t("mapa.filters.reset")}</Button>
        <Button size="sm" variant="outline" className="w-full" asChild>
          <Link to="/registrace-podniku">{t("mapa.addBusiness")}</Link>
        </Button>
      </div>

      <details className="group mt-5 rounded-md border border-border/60 bg-muted/30 p-2.5 sm:open:!block" open>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground [&::-webkit-details-marker]:hidden">
          <span className="truncate">{t("mapa.legend.title") || "Legenda"}</span>
          <svg
            viewBox="0 0 20 20"
            className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180"
            aria-hidden="true"
          >
            <path d="M5 7l5 6 5-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </summary>
        <TooltipProvider delayDuration={150}>
          <ul className="mt-2 space-y-1 text-xs">
            {([
              {
                key: "breweries",
                active: showBreweries,
                toggle: () => setShowBreweries((v) => !v),
                hint: t("mapa.breweriesHint"),
                label: t("mapa.breweries"),
                icon: (
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] text-white" style={{ background: "#b54225" }}>🍺</span>
                ),
              },
              {
                key: "partners",
                active: showPartners,
                toggle: () => setShowPartners((v) => !v),
                hint: t("mapa.partnerHint"),
                label: t("mapa.partnerBadge"),
                icon: (
                  <span className="relative grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] text-white" style={{ background: "#b54225" }}>
                    🍺
                    <span className="absolute -right-1 -top-1 grid h-3 w-3 place-items-center rounded-full bg-amber-400 text-[8px] font-bold text-amber-900">★</span>
                  </span>
                ),
              },
              {
                key: "anon",
                active: showAnon,
                toggle: () => setShowAnon((v) => !v),
                hint: t("mapa.anonPrivacyNote"),
                label: t("mapa.showAnon"),
                icon: (
                  <svg viewBox="0 0 32 44" className="h-5 w-4 shrink-0"><path d="M16 43 C6 30 2 22 2 14 A14 14 0 1 1 30 14 C30 22 26 30 16 43 Z" fill="#64748b" stroke="#fff" strokeWidth="2"/></svg>
                ),
              },
              {
                key: "members",
                active: showMembers,
                toggle: () => setShowMembers((v) => !v),
                hint: t("mapa.memberPrivacyNote"),
                label: t("mapa.showUsers"),
                icon: (
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] text-white" style={{ background: "#b54225" }}>👤</span>
                ),
              },
              {
                key: "membersNoBusiness",
                active: showMembersNoBusiness,
                toggle: () => setShowMembersNoBusiness((v) => !v),
                hint: t("mapa.memberNoBusiness.hint"),
                label: t("mapa.showMembersNoBusiness"),
                icon: (
                  <span className="relative grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] text-white" style={{ background: "#b54225" }}>
                    👤
                    <span className="absolute -right-1 -top-1 grid h-3 w-3 place-items-center rounded-full bg-blue-500 text-[8px] font-bold text-white">+</span>
                  </span>
                ),
              },
            ] as const).map((item) => (
              <li key={item.key}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={item.toggle}
                      onMouseEnter={() => highlightLegend(item.key)}
                      onMouseLeave={() => highlightLegend(null)}
                      onFocus={() => highlightLegend(item.key)}
                      onBlur={() => highlightLegend(null)}
                      aria-pressed={item.active}
                      className={`flex w-full items-center gap-2 rounded-md border px-2 py-1 text-left transition ${
                        item.active
                          ? "border-foreground/20 bg-background"
                          : "border-transparent bg-transparent opacity-50 hover:opacity-80"
                      }`}
                    >
                      {item.icon}
                      <span className="min-w-0 flex-1 truncate font-medium">{item.label}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">{item.hint}</TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>

        </TooltipProvider>

      </details>


    </>
  );

  return (
    <div className="flex h-screen w-full flex-col bg-background">
      <header className="flex items-center justify-between gap-2 border-b bg-background/95 px-3 py-2.5 backdrop-blur sm:px-4 sm:py-3">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <Link to="/" className="truncate font-serif text-base sm:text-lg">Cerveza.International</Link>
          <Badge variant="secondary" className="hidden sm:inline-flex">{t("mapa.tag")}</Badge>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground sm:text-xs">
            {isLoading ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> <span className="hidden sm:inline">{t("mapa.loading")}</span></>
            ) : (
              <><MapPin className="h-3 w-3" /> {filtered.length}/{pins.length}</>
            )}
          </div>
          <MapAuditBadge
            audit={mapAudit}
            renderedCount={catalogPins.length}
            auditRows={auditRows}
            renderedIds={useMemo(() => new Set(catalogPins.map((p) => p.id)), [catalogPins])}
          />
          <CountryCoverageBadge rows={countryCoverage} />
          <MyLocationStatus />
          <LanguageSwitcher />
          {/* Mobile filters trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button size="sm" variant="outline" className="md:hidden gap-1.5">
                <SlidersHorizontal className="h-4 w-4" /> {t("mapa.filters.title")}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto bg-muted/30 p-4">
              <SheetHeader>
                <SheetTitle>{t("mapa.filters.title")}</SheetTitle>
              </SheetHeader>
              <div className="mt-4">{filtersPanel}</div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden w-72 shrink-0 overflow-y-auto border-r bg-muted/30 p-4 md:block">
          {filtersPanel}
        </aside>


        <div className="relative flex-1">
          <div ref={containerRef} className="absolute inset-0" />
          <ReferrerMessageDialog userId={msgTarget} lang={lang} onClose={() => setMsgTarget(null)} />
          {!isLoading && filtered.length === 0 && (
            <Card className="absolute left-1/2 top-1/2 z-[400] -translate-x-1/2 -translate-y-1/2 p-4 text-sm shadow-lg">
              {t("mapa.empty.filtered")} <button className="underline" onClick={resetFilters}>{t("mapa.empty.resetFilters")}</button>
            </Card>
          )}
          {!publicMode && (import.meta.env.DEV || debugAllowed) && (
            <MapDebugPanel
              catalogPins={catalogPins}
              filteredCatalog={filteredCatalog}
              country={country}
              city={city}
              search={search}
              showBreweries={showBreweries}
            />
          )}
        </div>
      </div>


      <style>{`
        .lc-pin-dot {
          width: 32px; height: 32px; display: grid; place-items: center;
          border-radius: 50% 50% 50% 0; transform: rotate(-45deg);
          color: white; font-size: 14px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3); border: 2px solid white;
        }
        .lc-pin-dot > * { transform: rotate(45deg); }
        .lc-pin-star {
          position: absolute; top: -6px; right: -6px;
          width: 16px; height: 16px; border-radius: 50%;
          background: #fbbf24; color: #78350f;
          display: grid; place-items: center; font-size: 11px; font-weight: 700;
          border: 1.5px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        .leaflet-container { font-family: inherit; }
        .lc-user-pin-wrap {
          position: relative; width: 44px; height: 44px;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        }
        .lc-user-pin-bg {
          position: absolute; inset: 0; width: 100%; height: 100%;
          border-radius: 50%; object-fit: cover;
        }
        .lc-user-pin-mascot {
          position: absolute; inset: 4px; width: calc(100% - 8px); height: calc(100% - 8px);
          object-fit: contain;
        }
        .lc-legend-dim .leaflet-marker-icon { opacity: 0.2; transition: opacity 120ms ease, transform 120ms ease, filter 120ms ease; }
        .lc-legend-dim .leaflet-marker-icon.lc-legend-hi {
          opacity: 1;
          transform: scale(1.18);
          filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.85));
          z-index: 10000 !important;
        }
      `}</style>
    </div>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string
  ));
}

// Primary flag color per ISO-3166 alpha-2 country code.
// Picked from the dominant / most recognizable hue of each flag so pin
// heads visually correspond to the visitor's country.
const FLAG_COLORS: Record<string, string> = {
  CZ: "#11457e", SK: "#0b4ea2", PL: "#dc143c", DE: "#000000", AT: "#ed2939",
  HU: "#ce2939", RO: "#002b7f", BG: "#00966e", HR: "#171796", SI: "#005ce5",
  RS: "#c6363c", GR: "#0d5eaf", IT: "#009246", FR: "#0055a4", ES: "#aa151b",
  PT: "#046a38", BE: "#fdda24", NL: "#ae1c28", LU: "#00a1de", CH: "#da291c",
  LI: "#002b7f", IE: "#169b62", GB: "#c8102e", UK: "#c8102e", DK: "#c60c30",
  SE: "#005b99", NO: "#ba0c2f", FI: "#003580", IS: "#02529c", EE: "#0072ce",
  LV: "#9e1b32", LT: "#fdb913", BY: "#c8313e", UA: "#005bbb", MD: "#0033a0",
  RU: "#d52b1e", TR: "#e30a17", MT: "#c8102e", CY: "#d47600", AL: "#e41e20",
  MK: "#d20000", BA: "#002395", ME: "#c8102e", XK: "#244aa5",
  US: "#3c3b6e", CA: "#ff0000", MX: "#006847",
  BR: "#009c3b", AR: "#75aadb", CL: "#0033a0", CO: "#fcd116", PE: "#d91023",
  VE: "#fcd116", UY: "#0038a8", EC: "#ffdd00", BO: "#d52b1e", PY: "#d52b1e",
  CN: "#de2910", JP: "#bc002d", KR: "#003478", KP: "#024fa2", VN: "#da251d",
  TH: "#a51931", ID: "#ff0000", MY: "#cc0001", PH: "#0038a8", SG: "#ed2939",
  IN: "#ff9933", PK: "#01411c", BD: "#006a4e", LK: "#8d153a", NP: "#dc143c",
  AF: "#000000", IR: "#239f40", IQ: "#ce1126", SA: "#006c35", AE: "#00732f",
  IL: "#0038b8", JO: "#000000", LB: "#ed1c24", SY: "#ce1126", KW: "#007a3d",
  QA: "#8a1538", BH: "#ce1126", OM: "#008000", YE: "#ce1126",
  EG: "#ce1126", MA: "#c1272d", DZ: "#006233", TN: "#e70013", LY: "#239e46",
  ZA: "#007a4d", NG: "#008751", KE: "#006600", ET: "#078930", GH: "#006b3f",
  SN: "#00853f", CI: "#f77f00", CM: "#007a5e", TZ: "#1eb53a", UG: "#000000",
  AU: "#012169", NZ: "#012169", FJ: "#68bfe5",
};

function flagColorFor(cc: string): string {
  if (cc && FLAG_COLORS[cc]) return FLAG_COLORS[cc];
  // Deterministic fallback: hash country code to a hue so unknown countries
  // still get a stable, distinguishable color.
  let h = 0;
  for (let i = 0; i < cc.length; i++) h = (h * 31 + cc.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 65%, 42%)`;
}



function MyLocationStatus() {
  const { t } = useT();
  const qc = useQueryClient();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    void supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data } = supabase.auth.onAuthStateChange((_e, session) => setSignedIn(!!session));
    unsub = () => data.subscription.unsubscribe();
    return () => unsub?.();
  }, []);

  const { data: status, isFetching } = useQuery({
    queryKey: ["my-approx-location-status"],
    queryFn: () => getMyApproxLocationStatus(),
    enabled: signedIn === true,
    staleTime: 30_000,
  });

  const refresh = useMutation({
    mutationFn: () => updateMyApproxLocation(),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["my-approx-location-status"] });
      await qc.invalidateQueries({ queryKey: ["public-user-locations"] });
      if (res?.ok) {
        toast.success(t("mapa.location.toastOk"), {
          description: res.source === "cached"
            ? t("mapa.location.toastCached")
            : `${t("mapa.location.toastSource")}: ${res.source}${res.country ? ` · ${res.country}` : ""}`,
        });
      } else {
        toast.error(t("mapa.location.toastFail"), {
          description: t("mapa.location.toastFailDesc"),
        });
      }
    },
    onError: () => toast.error(t("mapa.location.toastError")),
  });

  // Auto-fill: as soon as we know the signed-in user has no coordinates
  // (or a stale value), trigger updateMyApproxLocation once without waiting
  // for a click. Guarded by a ref so we don't loop if the server returns
  // no-geo — the manual "Refresh location" button is still available.
  const autoTriedRef = useRef(false);
  useEffect(() => {
    if (signedIn !== true || !status) return;
    if (status.hasCoords && status.fresh) return;
    if (autoTriedRef.current) return;
    if (refresh.isPending) return;
    autoTriedRef.current = true;
    refresh.mutate();
  }, [signedIn, status, refresh]);

  // Reset the auto-fill guard when the signed-in identity changes so a new
  // user in the same tab gets their own auto-fill attempt.
  useEffect(() => {
    autoTriedRef.current = false;
  }, [signedIn]);

  if (signedIn !== true || !status) return null;


  const missing = !status.hasCoords;
  const stale = status.hasCoords && !status.fresh;

  const label = missing
    ? t("mapa.location.missing")
    : stale
    ? t("mapa.location.stale")
    : t("mapa.location.fresh");

  const Icon = missing || stale ? AlertTriangle : CheckCircle2;
  const colorClass = missing
    ? "border-destructive/40 bg-destructive/10 text-destructive"
    : stale
    ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";

  const updatedText = status.updatedAt
    ? new Date(status.updatedAt).toLocaleString()
    : t("mapa.location.never");

  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`hidden items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium sm:inline-flex ${colorClass}`}
        title={`${t("mapa.location.lastSeen")}: ${updatedText}${status.country ? ` · ${status.country}` : ""}${status.showOnMap ? "" : ` · ${t("mapa.location.hidden")}`}`}
      >
        <Icon className="h-3 w-3" /> {label}
      </span>
      {(missing || stale) && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-[11px]"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending || isFetching}
        >
          {refresh.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          {t("mapa.location.refresh")}
        </Button>
      )}
    </div>
  );
}

function MapAuditBadge({
  audit,
  renderedCount,
  auditRows,
  renderedIds,
}: {
  audit: CatalogMapAudit | undefined;
  renderedCount: number;
  auditRows: CatalogMapAuditRow[];
  renderedIds: Set<string>;
}) {
  const { t } = useT();
  const missing = useMemo(
    () => auditRows.filter((r) => !renderedIds.has(r.id)),
    [auditRows, renderedIds],
  );
  if (!audit) return null;
  const expected = audit.expected_pins;
  const diff = expected - renderedCount;
  const ok = diff === 0 && missing.length === 0;
  const sitesUnpub = audit.db.sites_total - audit.db.sites_published;
  const sitesNoGps = audit.db.sites_published - audit.db.sites_published_with_gps;
  const venuesUnpub = audit.db.venues_total - audit.db.venues_published;
  const venuesNoGps = audit.db.venues_published - audit.db.venues_published_with_gps;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={ok ? "outline" : "destructive"}
          className="h-7 gap-1 px-2 text-[11px] sm:text-xs"
          title={t("mapa.audit.title")}
        >
          {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
          <span className="hidden sm:inline">{t("mapa.audit.badge")}</span>
          <span>{renderedCount}/{expected}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[95vw] text-xs">
        <div className="mb-2 flex items-center gap-2 font-medium text-sm">
          {ok ? (
            <><CheckCircle2 className="h-4 w-4 text-green-600" /> {t("mapa.audit.ok")}</>
          ) : (
            <><AlertTriangle className="h-4 w-4 text-destructive" /> {t("mapa.audit.mismatch")}: {Math.max(Math.abs(diff), missing.length)}</>
          )}
        </div>
        <div className="space-y-1.5">
          <Row label={t("mapa.audit.rendered")} value={renderedCount} />
          <Row label={t("mapa.audit.expected")} value={expected} strong />
          <div className="my-2 h-px bg-border" />
          <div className="text-muted-foreground">{t("mapa.audit.breweries")}</div>
          <Row label={t("mapa.audit.total")} value={audit.db.sites_total} />
          <Row label={t("mapa.audit.published")} value={audit.db.sites_published} />
          <Row label={t("mapa.audit.publishedGps")} value={audit.db.sites_published_with_gps} strong />
          {sitesUnpub > 0 && <Row label={t("mapa.audit.unpublished")} value={sitesUnpub} muted />}
          {sitesNoGps > 0 && <Row label={t("mapa.audit.noGps")} value={sitesNoGps} muted />}
          <div className="my-2 h-px bg-border" />
          <div className="text-muted-foreground">{t("mapa.audit.venues")}</div>
          <Row label={t("mapa.audit.total")} value={audit.db.venues_total} />
          <Row label={t("mapa.audit.published")} value={audit.db.venues_published} />
          <Row label={t("mapa.audit.publishedGps")} value={audit.db.venues_published_with_gps} strong />
          {venuesUnpub > 0 && <Row label={t("mapa.audit.unpublished")} value={venuesUnpub} muted />}
          {venuesNoGps > 0 && <Row label={t("mapa.audit.noGps")} value={venuesNoGps} muted />}
        </div>
        <div className="mt-3 border-t pt-2">
          <div className="mb-1 flex items-center justify-between">
            <div className="font-medium text-sm">{t("mapa.audit.missingHeader")}</div>
            <span className="tabular-nums text-muted-foreground">{missing.length}</span>
          </div>
          {missing.length === 0 ? (
            <div className="text-muted-foreground">{t("mapa.audit.missingNone")}</div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded border bg-muted/30 p-1.5">
              <ul className="space-y-1">
                {missing.slice(0, 200).map((r) => (
                  <li key={r.id} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{r.name || t("mapa.audit.unnamed")}</div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {r.layer === "venue" ? t("mapa.audit.rowVenue") : t("mapa.audit.rowBrewery")}
                        {r.city ? ` · ${r.city}` : ""}
                        {r.country_iso2 ? ` · ${r.country_iso2}` : ""}
                      </div>
                    </div>
                    <code className="shrink-0 text-[9px] text-muted-foreground">{r.id.slice(0, 8)}</code>
                  </li>
                ))}
              </ul>
              {missing.length > 200 && (
                <div className="mt-1 text-center text-[10px] text-muted-foreground">
                  {t("mapa.audit.showingFirst").replace("{n}", String(missing.length))}
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
function CountryCoverageBadge({ rows }: { rows: CountryCoverageRow[] }) {
  const { t } = useT();
  const [tab, setTab] = useState<"all" | "published" | "imported" | "not_imported">("all");
  const stats = useMemo(() => {
    const s = { published: 0, imported: 0, not_imported: 0, sites_gps: 0 };
    for (const r of rows) {
      if (r.status === "PUBLISHED") s.published += 1;
      else if (r.status === "IMPORTED_NOT_PUBLISHED") s.imported += 1;
      else s.not_imported += 1;
      s.sites_gps += r.sites_published_with_gps + r.venues_published_with_gps;
    }
    return s;
  }, [rows]);
  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    if (tab === "published") return rows.filter((r) => r.status === "PUBLISHED");
    if (tab === "imported") return rows.filter((r) => r.status === "IMPORTED_NOT_PUBLISHED");
    return rows.filter((r) => r.status === "NOT_IMPORTED");
  }, [rows, tab]);

  if (rows.length === 0) return null;

  const badgeVariant = stats.imported > 0 ? "destructive" : "outline";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={badgeVariant}
          className="h-7 gap-1 px-2 text-[11px] sm:text-xs"
          title={t("mapa.coverage.title")}
        >
          <MapPin className="h-3 w-3" />
          <span className="hidden sm:inline">{t("mapa.coverage.badge")}</span>
          <span>{stats.published}/{rows.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] max-w-[95vw] text-xs">
        <div className="mb-2 font-medium text-sm">{t("mapa.coverage.title")}</div>
        <div className="mb-2 grid grid-cols-3 gap-2 text-[11px]">
          <div className="rounded border bg-green-50 p-1.5 text-center dark:bg-green-950/30">
            <div className="font-semibold tabular-nums text-green-700 dark:text-green-400">{stats.published}</div>
            <div className="text-muted-foreground">{t("mapa.coverage.published")}</div>
          </div>
          <div className="rounded border bg-amber-50 p-1.5 text-center dark:bg-amber-950/30">
            <div className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">{stats.imported}</div>
            <div className="text-muted-foreground">{t("mapa.coverage.imported")}</div>
          </div>
          <div className="rounded border bg-muted/40 p-1.5 text-center">
            <div className="font-semibold tabular-nums">{stats.not_imported}</div>
            <div className="text-muted-foreground">{t("mapa.coverage.notImported")}</div>
          </div>
        </div>
        <div className="mb-2 flex gap-1">
          {([
            ["all", `${t("mapa.coverage.tabAll")} (${rows.length})`],
            ["published", `${t("mapa.coverage.tabPublished")} (${stats.published})`],
            ["imported", `${t("mapa.coverage.tabImported")} (${stats.imported})`],
            ["not_imported", `${t("mapa.coverage.tabMissing")} (${stats.not_imported})`],
          ] as const).map(([k, label]) => (
            <Button
              key={k}
              size="sm"
              variant={tab === k ? "default" : "outline"}
              className="h-6 flex-1 px-1.5 text-[10px]"
              onClick={() => setTab(k)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="max-h-80 overflow-y-auto rounded border bg-muted/20">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
              <tr className="text-left">
                <th className="px-1.5 py-1 font-medium">{t("mapa.coverage.thCountry")}</th>
                <th className="px-1.5 py-1 text-right font-medium">{t("mapa.coverage.thPublic")}</th>
                <th className="px-1.5 py-1 text-right font-medium">{t("mapa.coverage.thImported")}</th>
                <th className="px-1.5 py-1 font-medium">{t("mapa.coverage.thStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.iso2} className="border-t">
                  <td className="px-1.5 py-1">
                    <span className="mr-1 text-muted-foreground">{r.iso2}</span> {r.name_en}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums">
                    {r.sites_published_with_gps + r.venues_published_with_gps || "—"}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums text-muted-foreground">
                    {r.sites_total || "—"}
                  </td>
                  <td className="px-1.5 py-1">
                    {r.status === "PUBLISHED" && (
                      <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-950/50 dark:text-green-300">
                        <CheckCircle2 className="h-2.5 w-2.5" /> {t("mapa.coverage.statusPublished")}
                      </span>
                    )}
                    {r.status === "IMPORTED_NOT_PUBLISHED" && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                        <AlertTriangle className="h-2.5 w-2.5" /> {t("mapa.coverage.statusImported")}
                      </span>
                    )}
                    {r.status === "NOT_IMPORTED" && (
                      <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {t("mapa.coverage.statusMissing")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-2 py-3 text-center text-muted-foreground">
                    {t("mapa.coverage.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground">
          {t("mapa.coverage.footer")}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, value, strong, muted }: { label: string; value: number; strong?: boolean; muted?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span>
      <span className={strong ? "font-semibold tabular-nums" : "tabular-nums"}>{value.toLocaleString()}</span>
    </div>
  );
}


// Country name (from businesses filter, e.g. "Francie" / "France") → ISO2.
// Used by the debug panel to map the sidebar country filter onto catalog pins
// (which are keyed by ISO2). Only common cases; unknowns fall back to null.
const COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  "česká republika": "CZ", "cesko": "CZ", "česko": "CZ", "czechia": "CZ", "czech republic": "CZ",
  "slovensko": "SK", "slovakia": "SK",
  "polsko": "PL", "poland": "PL",
  "německo": "DE", "nemecko": "DE", "germany": "DE",
  "rakousko": "AT", "austria": "AT",
  "maďarsko": "HU", "madarsko": "HU", "hungary": "HU",
  "francie": "FR", "france": "FR",
  "španělsko": "ES", "spanelsko": "ES", "spain": "ES",
  "itálie": "IT", "italie": "IT", "italy": "IT",
  "velká británie": "GB", "velka britanie": "GB", "united kingdom": "GB", "uk": "GB",
  "belgie": "BE", "belgium": "BE",
  "nizozemsko": "NL", "netherlands": "NL", "holland": "NL",
  "dánsko": "DK", "dansko": "DK", "denmark": "DK",
  "švédsko": "SE", "svedsko": "SE", "sweden": "SE",
  "norsko": "NO", "norway": "NO",
  "finsko": "FI", "finland": "FI",
  "irsko": "IE", "ireland": "IE",
  "portugalsko": "PT", "portugal": "PT",
  "švýcarsko": "CH", "svycarsko": "CH", "switzerland": "CH",
  "usa": "US", "spojené státy": "US", "united states": "US",
};

function MapDebugPanel({
  catalogPins,
  filteredCatalog,
  country,
  city,
  search,
  showBreweries,
}: {
  catalogPins: CatalogMapPin[];
  filteredCatalog: CatalogMapPin[];
  country: string;
  city: string;
  search: string;
  showBreweries: boolean;
}) {
  const [open, setOpen] = useState(false);

  // country je nyní ISO2 kód (např. "FR"), sjednocený mezi businesses a
  // katalogem, takže není potřeba žádný překlad z názvu.
  const iso2 = country && country !== "__all__" ? country.toUpperCase() : null;

  const stats = useMemo(() => {
    const breweries = catalogPins.filter((p) => p.layer === "brewery_site");
    const venues = catalogPins.filter((p) => p.layer === "venue");
    const filteredBreweries = filteredCatalog.filter((p) => p.layer === "brewery_site");

    // Country-specific counts (from DB perspective — everything loaded).
    let countryLoaded = 0;
    let countryVerified = 0;
    let countryDiscovery = 0;
    let countryPendingReview = 0;
    let countryRejected = 0;
    if (iso2) {
      for (const p of breweries) {
        if (p.country_iso2 !== iso2) continue;
        countryLoaded += 1;
        switch (p.review_status) {
          case "VERIFIED": countryVerified += 1; break;
          case "PENDING_REVIEW": countryPendingReview += 1; break;
          case "REJECTED": countryRejected += 1; break;
          default: countryDiscovery += 1;
        }
      }
    }

    // Top 8 countries by brewery count so it's obvious where data lives.
    const perCountry = new Map<string, number>();
    for (const p of breweries) {
      const k = p.country_iso2 ?? "??";
      perCountry.set(k, (perCountry.get(k) ?? 0) + 1);
    }
    const top = [...perCountry.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    return {
      totalLoaded: catalogPins.length,
      breweriesLoaded: breweries.length,
      venuesLoaded: venues.length,
      filteredBreweries: filteredBreweries.length,
      filteredTotal: filteredCatalog.length,
      countryLoaded,
      countryVerified,
      countryDiscovery,
      countryPendingReview,
      countryRejected,
      top,
    };
  }, [catalogPins, filteredCatalog, iso2]);

  const hiddenByFilter = stats.breweriesLoaded - stats.filteredBreweries;

  return (
    <div className="pointer-events-auto absolute bottom-3 left-3 right-3 z-[500] sm:right-auto sm:max-w-[92vw]">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="rounded-md border bg-background/95 px-2.5 py-1.5 text-[11px] font-medium shadow-md backdrop-blur hover:bg-background"
          title="Zobrazit ladicí panel"
        >
          🐞 Debug · {stats.filteredBreweries}/{stats.breweriesLoaded}
        </button>
      ) : (
        <div className="w-full max-h-[70vh] overflow-y-auto rounded-md border bg-background/95 p-3 text-xs shadow-lg backdrop-blur sm:w-[320px] sm:max-h-none sm:overflow-visible">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-semibold text-sm">🐞 Ladicí panel mapy</div>
            <button
              onClick={() => setOpen(false)}
              className="rounded px-1.5 text-muted-foreground hover:bg-muted"
              aria-label="Zavřít"
            >
              ×
            </button>
          </div>

          <div className="mb-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Načteno z DB</div>
            <Row label="Pivovary (brewery_site)" value={stats.breweriesLoaded} strong />
            <Row label="Provozovny (venue)" value={stats.venuesLoaded} />
            <Row label="Celkem pinů" value={stats.totalLoaded} muted />
          </div>

          <div className="mb-2 space-y-1 border-t pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Po aplikaci filtrů</div>
            <Row label="Viditelných pivovarů" value={stats.filteredBreweries} strong />
            {hiddenByFilter > 0 && (
              <Row label="Skrytých filtry" value={hiddenByFilter} muted />
            )}
            <div className="mt-1 space-y-0.5 text-[10px] text-muted-foreground">
              <div>Vrstva pivovarů: {showBreweries ? "✅ zapnutá" : "⛔ vypnutá"}</div>
              <div>Země (filtr): {country && country !== "__all__" ? `${country}${iso2 ? ` (${iso2})` : " — ISO neznámé"}` : "všechny"}</div>
              <div>Město (filtr): {city && city !== "__all__" ? city : "všechna"}</div>
              <div>Hledaný text: {search.trim() ? `„${search.trim()}"` : "—"}</div>
            </div>
          </div>

          {iso2 && (
            <div className="mb-2 space-y-1 border-t pt-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Země {iso2} — načteno z DB
              </div>
              <Row label="Celkem pivovarů" value={stats.countryLoaded} strong />
              {stats.countryVerified > 0 && <Row label="VERIFIED" value={stats.countryVerified} />}
              {stats.countryPendingReview > 0 && <Row label="PENDING_REVIEW" value={stats.countryPendingReview} />}
              {stats.countryDiscovery > 0 && <Row label="DISCOVERY_ONLY" value={stats.countryDiscovery} muted />}
              {stats.countryRejected > 0 && <Row label="REJECTED" value={stats.countryRejected} muted />}
              {country && country !== "__all__" && !iso2 && (
                <div className="text-[10px] text-amber-700">
                  Země „{country}" nemá mapování na ISO2 – ladicí rozpad nedostupný.
                </div>
              )}
            </div>
          )}

          <div className="space-y-1 border-t pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Top 8 zemí (pivovary v DB)
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 tabular-nums">
              {stats.top.map(([code, n]) => (
                <div key={code} className={`flex items-center justify-between ${iso2 === code ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                  <span>{code}</span>
                  <span>{n.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-2 border-t pt-2 text-[10px] text-muted-foreground">
            Vypnuté typy podniků neovlivňují pivovary; katalog pivovarů reaguje jen na filtr měst, textu a přepínač „Pivovary".
          </div>
        </div>
      )}
    </div>
  );
}

